import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Query, Mutation } from 'react-apollo';
import cn from 'classnames';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { push } from 'connected-react-router';
import { ActivityDetails } from 'containers';
import {
  CardList,
  DataList,
  MapComponent,
  ErrorBoundary,
  Online,
  Modal,
  Button,
  MessageBar,
} from 'common/components';
import { warningcircle } from 'common/svg/icons';
import {
  FETCH_STORAGE_ACTIVITY_WITH_FIELDS,
  FETCH_HARVESTING_ACTIVITY_WITH_FIELDS,
  FETCH_DELIVERY_ACTIVITY_WITH_FIELDS,
  FETCH_ALL_ACTIVITIES_WITH_FIELDS,
  MUTATION_DELETE_FIELD_ACTIVITY,
  MUTATION_DELETE_STORAGE_ACTIVITY,
  MUTATION_DELETE_DELIVERY_ACTIVITY,
} from 'api/activity';
import { FETCH_FIELD_DETAILS_WITH_NAME } from 'api/field';
import { MUTATION_DELETE_CROP } from 'api/crop';
import { date, toCommaNumber } from 'common/formatters';
import {
  ACTIVITY_TYPES,
  BANNER_IDS,
  ROUTE_ACTIVITY,
  UPDATE_MAP_FIELDS,
  OTHER_ACTIVITY_TYPES,
} from 'common/constants';
import { getFieldsListFromData } from 'api/util';
import { base64_decode } from 'util/base64';
import { capitalizeString, isDesktop, sumArray } from 'common/utils';
import * as actions from 'actions';
import { generateFieldId } from 'util/ids';
import { updateMaxPlantingArea } from 'actions/settings';
import { base64url_encode } from 'util/base64';

const desktop = isDesktop();

export class ActivityDetail extends PureComponent {
  state = {
    confirmActive: false,
    deleteActivityResponse: '',
  };

  _getFieldMapFragment({
    field,
    crops = [],
    getAllStorages,
    activity: { activityType, fieldActivityType, source },
    getAllFields,
    location,
  }) {
    if (fieldActivityType) {
      const filteredCrops = crops.filter(item => item.fieldName === field.name);
      return (
        field && (
          <MapComponent
            ignoreMapUpdate
            ignorePreservedBounds
            ignorePreservedLocation
            ignorePreservedZoom
            marker={location}
            selectedField={field.shape}
            polygons={[
              {
                geojson: field.geojson ? JSON.parse(field.geojson) : null,
                paths: field.shape,
                title: field.name,
                size: field.size,
                crops: filteredCrops,
              },
            ]}
          />
        )
      );
    } else if (activityType === ACTIVITY_TYPES.STORAGE) {
      const targetStorage = getAllStorages.find(
        storage => storage.id === source.id
      );
      return (
        <MapComponent
          showStorages
          showStorageMarkers
          useMaxZoomService
          ignoreMapUpdate
          defaultZoom={18}
          storages={[targetStorage]}
        />
      );
    } else if (activityType === ACTIVITY_TYPES.DELIVERY) {
      const fieldList = getFieldsListFromData({
        getAllCropsForField: crops,
        getAllFields,
      });
      return (
        <MapComponent
          ignoreMapUpdate
          showFields
          polygons={
            fieldList
              ? fieldList.map(item => ({
                  geojson: item.geojson ? JSON.parse(item.geojson) : null,
                  paths: item.shape,
                  title: item.name,
                  size: item.size,
                  disabled: item.disabled || false,
                  crops: item.crops || [],
                }))
              : []
          }
          storages={getAllStorages}
        />
      );
    }
  }

  _renderFertilizers = fertilizers =>
    (fertilizers || []).map(
      (
        {
          name,
          value: { nitrogen, phosphorus, potassium, zinc, sulfur, notes },
        },
        i
      ) => (
        <Fragment key={`${name}--${i}`}>
          <strong>{name}</strong>
          <p className="cardlist__subtitle cardlist__subtitle--fertilizers">
            {`N ${nitrogen} : P ${phosphorus} : K ${potassium} : Zn ${zinc} : S ${sulfur}`}
            {notes && <span style={{ display: 'block' }}>{notes}</span>}
          </p>
        </Fragment>
      )
    );

  _getCardData(activity) {
    const type =
      (activity && activity.fieldActivityType) || activity.activityType;
    let dest, qty;
    let data = [];

    if (type === ACTIVITY_TYPES.HARVESTING) {
      dest = type && activity[type.toLowerCase()];
      qty = {
        type: dest[0].amount.type,
        amount: dest.map(({ amount }) => amount.size),
      };

      data = [
        {
          label: 'Date',
          lines: [{ content: <strong>{date(activity.activityDate)}</strong> }],
        },
        {
          label: `Total ${activity.fieldActivityType}`,
          lines: [
            {
              content: (
                <strong>
                  {toCommaNumber(
                    qty &&
                      qty.amount &&
                      qty.amount.reduce((sum, current) => sum + current)
                  )}
                </strong>
              ),
              unit: qty.type,
            },
          ],
        },
        {
          label: 'Destination',
          lines:
            dest &&
            dest.map((bin, idx) => {
              const {
                destination: { name },
                amount: { size, type },
              } = bin;
              return {
                content: <strong key={idx}>{name}</strong>,
                unit: (
                  <Fragment>
                    <output>{toCommaNumber(size)}</output> {type}
                  </Fragment>
                ),
              };
            }),
        },
      ];
    } else if (type === ACTIVITY_TYPES.STORAGE) {
      data = [
        {
          label: 'Date',
          lines: [{ content: <strong>{date(activity.activityDate)}</strong> }],
        },
        {
          label: 'Destination',
          lines:
            activity.destinations &&
            activity.destinations.map((destination, idx) => {
              const {
                destination: { name },
                amount: { size, type },
              } = destination;
              return {
                content: <strong key={idx}>{name}</strong>,
                unit: (
                  <Fragment>
                    <output>{toCommaNumber(size)}</output> {type}
                  </Fragment>
                ),
              };
            }),
        },
      ];
    } else if (type === ACTIVITY_TYPES.DELIVERY) {
      data = [
        {
          label: 'Source',
          lines: [
            {
              content: (
                <strong>{activity.source && activity.source.name}</strong>
              ),
              unit: (
                <Fragment>
                  <output>
                    {toCommaNumber(activity.destination.amount.size)}
                  </output>{' '}
                  {activity.destination && activity.destination.amount.type}
                </Fragment>
              ),
            },
          ],
        },
      ];
    } else if (type === ACTIVITY_TYPES.SCOUTING) {
      data = [
        {
          label: 'Type',
          lines: [
            {
              content: (
                <strong>
                  {capitalizeString(activity.fieldActivityType.toLowerCase())}
                </strong>
              ),
            },
          ],
        },
        {
          label: 'Field',
          lines: [{ content: <strong>{activity.fieldName}</strong> }],
        },
      ];

      // for main scouting activity
      activity.observations.map(observation => {
        observation &&
          data.push({
            label: observation.name,
            lines: [
              { content: <strong>{observation.value.severity}</strong> },
              { content: observation.value.notes || '' },
            ],
          });
      });

      // For date
      data.push({
        label: 'Date',
        lines: [{ content: <strong>{date(activity.activityDate)}</strong> }],
      });
    } else if (type === ACTIVITY_TYPES.FERTILIZING) {
      data = [
        {
          label: 'Date',
          lines: [{ content: <strong>{date(activity.activityDate)}</strong> }],
        },
      ];

      if (activity.fertilizing && activity.fertilizing.length) {
        data.push({
          label: 'Fertilizer',
          lines: [
            {
              content: (
                <Fragment>
                  {this._renderFertilizers(activity.fertilizing)}
                </Fragment>
              ),
            },
          ],
        });
      }
    } else if (type === ACTIVITY_TYPES.SPRAYING) {
      data = [
        {
          label: 'Date',
          lines: [{ content: <strong>{date(activity.activityDate)}</strong> }],
        },
      ];

      if (activity.chemicals && activity.chemicals.length) {
        data.push({
          label: 'Chemicals',
          lines: [
            {
              content: (
                <Fragment>
                  {(activity.chemicals || []).map((chemical, i) => {
                    const {
                      name,
                      value: { brand, type, notes },
                    } = chemical;
                    return (
                      <Fragment key={`${name}--${i}`}>
                        <strong>{name}</strong>
                        <p className="cardlist__chemicals cardlist__chemicals--brand">
                          {brand}
                        </p>
                        <p className="cardlist__chemicals cardlist__chemicals--type">
                          {capitalizeString(type)}
                          {notes && (
                            <span style={{ display: 'block' }}>
                              Chemical Mix Rate {notes}
                            </span>
                          )}
                        </p>
                      </Fragment>
                    );
                  })}
                </Fragment>
              ),
            },
          ],
        });
      }

      if (activity.sprayingRate) {
        data = [
          ...data,
          {
            label: 'Spraying Rate',
            lines: [
              {
                content: <strong>{activity.sprayingRate}</strong>,
              },
            ],
          },
        ];
      }
    } else if (type === ACTIVITY_TYPES.SEEDING) {
      const {
        seeding: {
          seedingRate,
          expectedYield,
          chemical,
          inoculant,
          fertilizers,
        },
      } = activity;
      data = [
        {
          label: 'Seeding Date',
          lines: [{ content: <strong>{date(activity.activityDate)}</strong> }],
        },
        {
          label: 'Expected Yield',
          lines: [
            { content: <strong>{expectedYield[0].yield.size} Bu/Acre</strong> },
          ],
        },
      ];

      if (seedingRate) {
        data.splice(1, 0, {
          label: 'Seeding Rate',
          lines: [{ content: <strong>{seedingRate} seeds/acre</strong> }],
        });
      }

      if (chemical) {
        data.push({
          label: 'Seed Treatment Chemical',
          lines: [{ content: <strong>{chemical}</strong> }],
        });
      }

      if (inoculant) {
        data.push({
          label: 'Seed Treatment Inoculant',
          lines: [{ content: <strong>{inoculant}</strong> }],
        });
      }

      if (fertilizers && fertilizers.length) {
        data.push({
          label: 'Fertilizer',
          lines: [
            {
              content: (
                <Fragment>{this._renderFertilizers(fertilizers)}</Fragment>
              ),
            },
          ],
        });
      }
    } else if (
      type !== ACTIVITY_TYPES.HARVESTING &&
      type !== ACTIVITY_TYPES.STORAGE &&
      type !== ACTIVITY_TYPES.DELIVERY &&
      type !== ACTIVITY_TYPES.SCOUTING
    ) {
      data = [
        {
          label: 'Date',
          lines: [{ content: <strong>{date(activity.activityDate)}</strong> }],
        },
        {
          label: 'Type',
          lines: [
            {
              content: (
                <strong>
                  {capitalizeString(activity.fieldActivityType.toLowerCase())}
                </strong>
              ),
            },
          ],
        },
        {
          label: 'Field',
          lines: [{ content: <strong>{activity.fieldName}</strong> }],
        },
      ];
    }
    if (activity.notes) {
      data = data.concat([
        {
          label: 'Notes',
          lines: [
            { content: <p className="text--medium">{activity.notes}</p> },
          ],
        },
      ]);
    }
    return data;
  }

  /**
   *
   * Fetch cached data from graphql store
   * @memberof ActivityDetail
   * @function _getCachedData
   */
  _getCachedData = client => {
    const {
      season,
      match: {
        params: { encodedId },
      },
    } = this.props;
    const id = base64_decode(encodedId);
    const activityType = id.split(':')[1];

    // read cache
    const {
      getAllActivitiesForDelivery,
      getAllActivitiesForField,
      getAllActivitiesForStorage,
      getAllFields,
      getAllCropsForField,
      getAllStorages,
    } =
      client.readQuery({
        query: FETCH_ALL_ACTIVITIES_WITH_FIELDS,
        variables: { season, active: true },
      }) || {};

    let allActivities = [];

    if (activityType === ACTIVITY_TYPES.STORAGE) {
      allActivities = getAllActivitiesForStorage;
    } else if (activityType === 'FIELD') {
      allActivities = getAllActivitiesForField;
    } else if (activityType === ACTIVITY_TYPES.DELIVERY) {
      allActivities = getAllActivitiesForDelivery;
    }

    return {
      cachedActivity: allActivities.find(a => a.id === id),
      getAllFields,
      getAllCropsForField,
      getAllStorages,
    };
  };

  _renderFieldMap = component =>
    desktop ? component : <Online>{component}</Online>;

  _toggleActivityModal = () =>
    this.setState({ confirmActive: !this.state.confirmActive });

  _handleUpdateCache = (cache, activity) => {
    const {
      season,
      match: {
        params: { encodedId },
      },
      goToPage,
      isOnline,
    } = this.props;
    const id = base64_decode(encodedId);
    // use closures to avoid variable name conflicts
    // update activity list offline
    (function updateActivityList() {
      const { getAllActivitiesForField, ...rest } = cache.readQuery({
        query: FETCH_ALL_ACTIVITIES_WITH_FIELDS,
        variables: {
          season,
          active: true,
        },
      });

      const filteredActivities = getAllActivitiesForField.filter(
        activity => activity.id !== id
      );

      cache.writeQuery({
        query: FETCH_ALL_ACTIVITIES_WITH_FIELDS,
        variables: {
          season,
          active: true,
        },
        data: {
          getAllActivitiesForField: filteredActivities,
          ...rest,
        },
      });
    })();

    // update acivity list in field detail
    (function updateActivityListInFieldDetail() {
      const { fieldName } = activity;
      const queryVars = {
        season,
        active: true,
        name: fieldName,
        id: generateFieldId(fieldName),
      };
      const { getAllActivitiesForField, ...rest } = cache.readQuery({
        query: FETCH_FIELD_DETAILS_WITH_NAME,
        variables: queryVars,
      });

      const filteredActivities = getAllActivitiesForField.filter(
        activity => activity.id !== id
      );

      cache.writeQuery({
        query: FETCH_FIELD_DETAILS_WITH_NAME,
        variables: queryVars,
        data: {
          getAllActivitiesForField: filteredActivities,
          ...rest,
        },
      });
    })();

    // in offline mode, redirect user first to avoid page crash
    if (!isOnline) {
      goToPage(ROUTE_ACTIVITY);
    }
  };

  _getActivityModal = activity => {
    const { deleteActivityResponse } = this.state;
    const {
      season,
      match: {
        params: { encodedId },
      },
      displayBanner,
      goToPage,
      mapFields,
      updateMapFields,
    } = this.props;
    const id = base64_decode(encodedId);
    let cropId = '';

    if (activity.fieldActivityType === ACTIVITY_TYPES.SEEDING) {
      cropId = `CROPFIELD:${season}:${activity.fieldName}:${
        activity.crop.name
      }:${activity.crop.variety || ''}`;
    }

    let mutation;
    if (activity.fieldActivityType) {
      switch (activity.fieldActivityType) {
        case ACTIVITY_TYPES.SPRAYING:
        case ACTIVITY_TYPES.FERTILIZING:
        case ACTIVITY_TYPES.SCOUTING:
        case ACTIVITY_TYPES.SEEDING:
        case ACTIVITY_TYPES.HARVESTING:
        case OTHER_ACTIVITY_TYPES.TILLING:
        case OTHER_ACTIVITY_TYPES.IRRIGATION:
        case OTHER_ACTIVITY_TYPES.CUSTOM:
          mutation = MUTATION_DELETE_FIELD_ACTIVITY;
          break;
      }
    } else if (activity.activityType) {
      switch (activity.activityType) {
        case ACTIVITY_TYPES.STORAGE:
          mutation = MUTATION_DELETE_STORAGE_ACTIVITY;
          break;
        case ACTIVITY_TYPES.DELIVERY:
          mutation = MUTATION_DELETE_DELIVERY_ACTIVITY;
          break;
      }
    }

    const hasGraphQLError = !!deleteActivityResponse.message;

    return (
      this.state.confirmActive && (
        <Modal
          onToggle={this._toggleActivityModal}
          theme={['narrow']}
          header={'Delete Activity'}
        >
          {hasGraphQLError ? (
            <small>
              <strong>
                {/* according to current design.
                we show the general error message `The activity cannot be deleted` for BE error for now. */}
                This activity cannot be deleted.
              </strong>
            </small>
          ) : (
            <strong>Are you sure you want to delete this activity?</strong>
          )}

          {(activity.fieldActivityType === ACTIVITY_TYPES.HARVESTING ||
            activity.activityType === ACTIVITY_TYPES.STORAGE ||
            activity.activityType === ACTIVITY_TYPES.DELIVERY) && (
            <MessageBar
              SvgIcon={warningcircle}
              message="Grain transferred will be reverted"
              theme={['vertical-gap']}
            />
          )}

          {hasGraphQLError && (
            <small>
              <strong>
                For further assistance, please contact AGI Grow support at{' '}
                <a href="mailto:support@agigrow.com">support@agigrow.com</a>.
              </strong>
            </small>
          )}

          <section className="field__group">
            <fieldset className="field text--center">
              {mutation && (
                <Mutation
                  mutation={mutation}
                  onError={error => {
                    this.setState({
                      deleteActivityResponse: error,
                    });
                  }}
                  onCompleted={() => {
                    displayBanner(BANNER_IDS.APP, 'ACTIVITY DELETED');
                    goToPage(ROUTE_ACTIVITY);
                    this._toggleActivityModal();
                  }}
                  update={cache => this._handleUpdateCache(cache, activity)}
                >
                  {deleteActivityForField =>
                    !deleteActivityResponse && (
                      <Mutation
                        mutation={MUTATION_DELETE_CROP}
                        update={cache => {
                          const { fieldName } = activity;
                          const queryVars = {
                            season,
                            active: true,
                            name: fieldName,
                            id: generateFieldId(fieldName),
                          };
                          // update fields on map
                          const fields = mapFields.map(field => {
                            if (field && field.name === fieldName) {
                              return {
                                ...field,
                                crops: field.crops.filter(
                                  crop => crop.id !== cropId
                                ),
                              };
                            }
                            return field;
                          });
                          updateMapFields(fields);
                          // update query
                          const {
                            getAllCropsForField,
                            ...rest
                          } = cache.readQuery({
                            query: FETCH_FIELD_DETAILS_WITH_NAME,
                            variables: queryVars,
                          });

                          const filteredCrops = getAllCropsForField.filter(
                            crop => crop.id !== cropId
                          );

                          cache.writeQuery({
                            query: FETCH_FIELD_DETAILS_WITH_NAME,
                            variables: queryVars,
                            data: {
                              getAllCropsForField: filteredCrops,
                              ...rest,
                            },
                          });
                        }}
                      >
                        {deleteCropForField => (
                          <Button
                            className="btn btn--primary"
                            onClick={() => {
                              deleteActivityForField({ variables: { id } });

                              // delete crop for seeding activity
                              if (
                                activity.fieldActivityType ===
                                ACTIVITY_TYPES.SEEDING
                              ) {
                                deleteCropForField({
                                  variables: {
                                    id: cropId,
                                  },
                                });
                              }
                            }}
                          >
                            Yes
                          </Button>
                        )}
                      </Mutation>
                    )
                  }
                </Mutation>
              )}
            </fieldset>
            <fieldset className="field text--center">
              <Button
                className={
                  deleteActivityResponse.message ? 'btn btn--primary' : 'btn'
                }
                onClick={this._toggleActivityModal}
              >
                {deleteActivityResponse.message ? 'Ok' : 'No'}
              </Button>
            </fieldset>
          </section>
        </Modal>
      )
    );
  };

  goToEdit = ({ activity, crops, field, markAsDone }) => {
    const { goToPage } = this.props;
    if (activity.fieldActivityType === ACTIVITY_TYPES.SEEDING) {
      const otherCrops = crops.filter(
        c =>
          c.crop.name !== activity.crop.name &&
          c.crop.variety !== activity.crop.variety
      );
      const maxPlantingArea = toCommaNumber(
        field.size.size -
          sumArray(
            otherCrops.map(c => c && c.plantedArea && c.plantedArea.size)
          )
      );
      this.props.updateMaxPlantingArea(maxPlantingArea);
    }
    const isOtherActivity = OTHER_ACTIVITY_TYPES[activity.fieldActivityType];
    const activityType = activity.fieldActivityType || activity.activityType;

    goToPage(
      `${ROUTE_ACTIVITY}/${base64url_encode(activity.id)}/${
        isOtherActivity ? 'OTHER' : activityType
      }${markAsDone ? '/done' : ''}`
    );
  };

  render() {
    const {
      match: {
        params: { encodedId },
      },
      isOnline,
    } = this.props;
    const id = base64_decode(encodedId);
    let activityType = id.split(':')[1];

    let query;

    if (activityType === ACTIVITY_TYPES.STORAGE) {
      query = FETCH_STORAGE_ACTIVITY_WITH_FIELDS;
    } else if (activityType === 'FIELD') {
      query = FETCH_HARVESTING_ACTIVITY_WITH_FIELDS;
    } else if (activityType === ACTIVITY_TYPES.DELIVERY) {
      query = FETCH_DELIVERY_ACTIVITY_WITH_FIELDS;
    }

    return (
      <ErrorBoundary>
        <Query
          query={query}
          fetchPolicy="cache-and-network"
          variables={{ id, active: true }}
        >
          {({ loading, error, data, client }) => {
            if (isOnline && (loading || error)) return null;
            const activitiesFound = Object.keys(data || {}).every(k => data[k]);
            if (activitiesFound === false) return <Redirect to="/error" />;

            let cachedData = {};
            if (Object.keys(data || {}).length === 0) {
              cachedData = this._getCachedData(client);
            }

            const { cachedActivity } = cachedData;

            // if the fetched data is not available, use cached data
            const {
              getActivityForField,
              getActivityForStorage,
              getActivityForDelivery,
              getAllFields = cachedData.getAllFields,
              getAllCropsForField = cachedData.getAllCropsForField,
              getAllStorages = cachedData.getAllStorages,
            } = data || {};

            const activity =
              getActivityForField ||
              getActivityForStorage ||
              getActivityForDelivery ||
              cachedActivity;
            // For field activities (harvesting and other type, no season data in field)
            const field =
              activity &&
              getAllFields &&
              getAllFields.find(field => field.name === activity.fieldName);
            // For harvesting (no season data in field), used to get plantedArea
            const selectedCrop =
              activity.crop &&
              getAllCropsForField &&
              getAllCropsForField.find(
                crop =>
                  activity.crop.name === crop.crop.name &&
                  activity.crop.variety === crop.crop.variety
              );
            // pass crops to calculate max planting area
            const crops = getAllCropsForField
              ? getAllCropsForField.filter(
                  crop => crop.fieldName === activity.fieldName
                )
              : [];
            const location = activity && activity.location;

            return (
              <Fragment>
                <div
                  className={cn(
                    'field-wrapper__wrapper field-wrapper--mapstatic columns is-gapless',
                    id && 'columns--white'
                  )}
                >
                  {this._renderFieldMap(
                    this._getFieldMapFragment({
                      field,
                      crops: getAllCropsForField,
                      getAllStorages,
                      activity,
                      getAllFields,
                      location,
                    })
                  )}
                  <aside className="field-wrapper__aside column is-one-third is-gapless">
                    <ActivityDetails
                      field={field}
                      crops={crops}
                      activity={activity}
                      selectedCrop={selectedCrop}
                      deleteAction={this._toggleActivityModal}
                      goToEdit={() => this.goToEdit({ activity, crops, field })}
                    />
                    <div className={'column__inner'}>
                      <CardList
                        cards={[
                          {
                            content: (
                              <DataList
                                data={this._getCardData(activity)}
                                theme={['lead']}
                              />
                            ),
                          },
                        ]}
                      />
                    </div>
                    {!activity.finishDate && (
                      <fieldset className={'field text--center'}>
                        <Button
                          className="btn btn--primary"
                          onClick={() =>
                            this.goToEdit({
                              activity,
                              crops,
                              field,
                              markAsDone: true,
                            })
                          }
                        >
                          MARK AS DONE
                        </Button>
                      </fieldset>
                    )}
                  </aside>
                </div>
                {this._getActivityModal(activity)}
              </Fragment>
            );
          }}
        </Query>
      </ErrorBoundary>
    );
  }
}

ActivityDetail.propTypes = {
  match: PropTypes.object,
  history: PropTypes.object,
  field: PropTypes.object,
  season: PropTypes.number.isRequired,
  isOnline: PropTypes.bool.isRequired,
  displayBanner: PropTypes.func.isRequired,
  goToPage: PropTypes.func.isRequired,
  mapFields: PropTypes.arrayOf(PropTypes.object).isRequired,
  updateMapFields: PropTypes.func.isRequired,
  updateMaxPlantingArea: PropTypes.func.isRequired,
};

const mapState = ({ settings: { season, isOnline }, map: { fields } }) => ({
  season,
  isOnline,
  mapFields: fields,
});

const mapDispatch = {
  displayBanner: actions.banner.display,
  goToPage: routeName => push(routeName),
  updateMapFields: fields => ({
    type: UPDATE_MAP_FIELDS,
    payload: fields,
  }),
  updateMaxPlantingArea: paylod => updateMaxPlantingArea(paylod),
};

export default connect(
  mapState,
  mapDispatch
)(ActivityDetail);



// WEBPACK FOOTER //
// ./src/pages/Activity/Detail/index.js